'use client';

import { useEffect } from 'react';

export default function LogInterceptor() {
  useEffect(() => {
    // 1. Helper function to send errors to the backend
    const sendToServer = (message: string, stack?: string) => {
      fetch('/api/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, stack }),
      }).catch((err) => {
        // Fallback to original console to avoid loops
        originalConsoleError('Failed to send log to server:', err);
      });
    };

    // 2. Intercept manual console.error calls
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      originalConsoleError.apply(console, args);

      const message = args
        .map((arg) => (arg instanceof Error ? arg.message : typeof arg === 'object' ? JSON.stringify(arg) : arg))
        .join(' ');

      const stack = args.find((arg) => arg instanceof Error)?.stack || new Error().stack;
      
      sendToServer(message, stack);
    };

    // 3. Catch standard runtime errors (e.g., undefined variables)
    const handleRuntimeError = (event: ErrorEvent) => {
      sendToServer(
        `[RUNTIME UNHANDLED] ${event.message}`, 
        event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`
      );
    };

    // 4. Catch unhandled async Promise rejections
    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      const stack = reason instanceof Error ? reason.stack : 'No stack trace';
      
      sendToServer(`[UNHANDLED REJECTION] ${message}`, stack);
    };

    // Attach listeners
    window.addEventListener('error', handleRuntimeError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    // Cleanup interceptors on unmount
    return () => {
      console.error = originalConsoleError;
      window.removeEventListener('error', handleRuntimeError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  return null;
}
